import React, { useEffect } from "react";
import { AiOutlineInfoCircle } from "react-icons/ai";
import { DocumentText1 } from "iconsax-react";
import { Banner } from "Components/Generic/Banner/Banner";
import { ButtonsContainer, Container, SkeletonContainer } from "./styles";
import { ParentAccordion } from "./components/ParentAccordion";
import { ChildAccordion } from "./components/ChildAccordion";
import { useNetworkSettings } from "./hooks/useNetworkSettings";
import { FieldGroup } from "./types";
import { Color } from "Components/Form/Button";
import { useTranslation } from "react-i18next";
import { useNetworkSettingsActions } from "./useAction";
import { NotificationToast } from "Components/Generic/Toast/NotificationToast";
import ActionButtons, { ActionButtonProps } from "Components/Generic/ActionButtons";
import { CompanyConfig } from "Models/company.config";
import { Skeleton } from "@mui/material";

export const NetworkSettingsPage = () => {
  const { t: translate } = useTranslation();

  const {
    accordionStates,
    fieldOptions,
    fieldStates,
    fieldHandlers,
    selectAllHandlers,
    orderStatus,
    getNetworkConfig,
    resetToInitialState,
    loadConfigFromAPI,
  } = useNetworkSettings();

  const { getNetworkConfigs, saveNetworkConfigs } = useNetworkSettingsActions();
  const { data: companyConfig, isFetching } = getNetworkConfigs();
  const { mutate: saveConfig, isLoading: isSaving } = saveNetworkConfigs({
    handleSuccess() {
      NotificationToast.success(translate("updatedSuccessfully"));
    },
  });

  useEffect(() => {
    if (companyConfig) {
      loadConfigFromAPI(companyConfig);
    }
  }, [companyConfig, loadConfigFromAPI]);

  const detailFieldGroups: FieldGroup[] = [
    {
      title: translate("networkDetails"),
      options: fieldOptions.details,
      selectedValues: fieldStates.detailsSelected,
      onChangeHandler: fieldHandlers.handleDetailsChange,
    },
    {
      title: translate("networkRoles"),
      options: fieldOptions.roles,
      selectedValues: fieldStates.rolesSelected,
      onChangeHandler: fieldHandlers.handleRolesChange,
    },
    {
      title: translate("networkWeightAndMeasurements"),
      options: fieldOptions.weightMeasurements,
      selectedValues: fieldStates.weightMeasurementsSelected,
      onChangeHandler: fieldHandlers.handleWeightMeasurementsChange,
    },
    {
      title: translate("networkPickup"),
      options: fieldOptions.pickup,
      selectedValues: fieldStates.pickupSelected,
      onChangeHandler: fieldHandlers.handlePickupChange,
    },
    {
      title: translate("networkDelivery"),
      options: fieldOptions.delivery,
      selectedValues: fieldStates.deliverySelected,
      onChangeHandler: fieldHandlers.handleDeliveryChange,
    },
    {
      title: translate("networkGeneralComments"),
      options: fieldOptions.generalComments,
      selectedValues: fieldStates.generalCommentsSelected,
      onChangeHandler: fieldHandlers.handleGeneralCommentsChange,
      isLast: true,
    },
  ];

  const inventoryFieldGroups: FieldGroup[] = [
    {
      title: translate("networkActualInventory"),
      options: fieldOptions.actualInventory,
      selectedValues: fieldStates.actualInventorySelected,
      onChangeHandler: fieldHandlers.handleActualInventoryChange,
    },
    {
      title: translate("networkQuotedInventory"),
      options: fieldOptions.quotedInventory,
      selectedValues: fieldStates.quotedInventorySelected,
      onChangeHandler: fieldHandlers.handleQuotedInventoryChange,
      isLast: true,
    },
  ];

  const materialFieldGroups: FieldGroup[] = [
    {
      title: null,
      options: fieldOptions.material,
      selectedValues: fieldStates.materialSelected,
      onChangeHandler: fieldHandlers.handleMaterialChange,
      isLast: true,
    },
  ];

  const taskFieldGroups: FieldGroup[] = [
    {
      title: null,
      options: fieldOptions.task,
      selectedValues: fieldStates.taskSelected,
      onChangeHandler: fieldHandlers.handleTaskChange,
      isLast: true,
    },
  ];

  const accessFieldGroups: FieldGroup[] = [
    {
      title: null,
      options: fieldOptions.access,
      selectedValues: fieldStates.accessSelected,
      onChangeHandler: fieldHandlers.handleAccessChange,
      isLast: true,
    },
  ];

  const instructionFieldGroups: FieldGroup[] = [
    {
      title: null,
      options: fieldOptions.instruction,
      selectedValues: fieldStates.instructionSelected,
      onChangeHandler: fieldHandlers.handleInstructionChange,
      isLast: true,
    },
  ];

  const folderFieldGroups: FieldGroup[] = [
    {
      title: translate("networkPhotosAndVideos"),
      options: fieldOptions.photosVideos,
      selectedValues: fieldStates.photosVideosSelected,
      onChangeHandler: fieldHandlers.handlePhotosVideosChange,
    },
    {
      title: translate("networkDocuments"),
      options: fieldOptions.documents,
      selectedValues: fieldStates.documentsSelected,
      onChangeHandler: fieldHandlers.handleDocumentsChange,
      isLast: true,
    },
  ];

  const getSelectAllState = (groups: FieldGroup[]) => {
    const allOptions = groups.flatMap((group) => group.options);
    const allSelected = groups.flatMap((group) => group.selectedValues);
    const isAllSelected = allSelected.length === allOptions.length;
    const isPartiallySelected = allSelected.length > 0 && allSelected.length < allOptions.length;
    return { isAllSelected, isPartiallySelected };
  };

  const handleCancel = () => {
    if (companyConfig) {
      loadConfigFromAPI(companyConfig);
    } else {
      resetToInitialState();
    }
  };

  const handleSave = () => {
    if (!companyConfig) {
      console.error("No company config available to save");
      return;
    }

    const networkConfig = getNetworkConfig();

    const updatedCompanyConfig: CompanyConfig = {
      ...companyConfig,
      settings: {
        ...companyConfig.settings,
        networkConfiguration: networkConfig,
      },
    };

    saveConfig(updatedCompanyConfig);
  };

  const actionButtonProps: ActionButtonProps = {
    textCancel: translate("cancel"),
    textSave: translate("update"),
    customColorCancel: Color.CANCEL,
    customColorSave: Color.SUCCESS,
    onCancel: handleCancel,
    onSave: handleSave,
    cancelDisabled: isFetching,
    saveDisabled: isFetching,
    loading: isSaving,
    showCancelButton: true,
    showSaveButton: true,
  };

  const renderContent = () => {
    if (isFetching) {
      return (
        <SkeletonContainer>
          <Skeleton variant="rounded" height="46px" />
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton variant="rounded" height="56px" key={index} />
          ))}
        </SkeletonContainer>
      );
    }

    if (!companyConfig) {
      return (
        <div>
          {translate("systemConfigurationIsNotAvailablePleaseContactTheAdministratorForAssistance")}
        </div>
      );
    }

    return (
      <>
        <Banner
          type="info"
          prefix={<AiOutlineInfoCircle size={20} />}
          label={translate("networkSettingsBannerMessage")}
        />

        <ParentAccordion
          title={translate("order")}
          icon={<DocumentText1 />}
          status={orderStatus}
          expanded={accordionStates.orderExpanded}
          onToggle={() => accordionStates.setOrderExpanded(!accordionStates.orderExpanded)}
        >
          <ChildAccordion
            title={translate("detail")}
            expanded={accordionStates.detailExpanded}
            onToggle={() => accordionStates.setDetailExpanded(!accordionStates.detailExpanded)}
            fieldGroups={detailFieldGroups}
            onSelectAll={selectAllHandlers.handleDetailSelectAll}
            {...getSelectAllState(detailFieldGroups)}
          />

          <ChildAccordion
            title={translate("inventory")}
            expanded={accordionStates.inventoryExpanded}
            onToggle={() =>
              accordionStates.setInventoryExpanded(!accordionStates.inventoryExpanded)
            }
            fieldGroups={inventoryFieldGroups}
            onSelectAll={selectAllHandlers.handleInventorySelectAll}
            {...getSelectAllState(inventoryFieldGroups)}
          />

          <ChildAccordion
            title={translate("material")}
            expanded={accordionStates.materialExpanded}
            onToggle={() => accordionStates.setMaterialExpanded(!accordionStates.materialExpanded)}
            fieldGroups={materialFieldGroups}
            onSelectAll={selectAllHandlers.handleMaterialSelectAll}
            {...getSelectAllState(materialFieldGroups)}
          />

          <ChildAccordion
            title={translate("task")}
            expanded={accordionStates.taskExpanded}
            onToggle={() => accordionStates.setTaskExpanded(!accordionStates.taskExpanded)}
            fieldGroups={taskFieldGroups}
            onSelectAll={selectAllHandlers.handleTaskSelectAll}
            {...getSelectAllState(taskFieldGroups)}
          />

          <ChildAccordion
            title={translate("access")}
            expanded={accordionStates.accessExpanded}
            onToggle={() => accordionStates.setAccessExpanded(!accordionStates.accessExpanded)}
            fieldGroups={accessFieldGroups}
            onSelectAll={selectAllHandlers.handleAccessSelectAll}
            {...getSelectAllState(accessFieldGroups)}
          />

          <ChildAccordion
            title={translate("instruction")}
            expanded={accordionStates.instructionExpanded}
            onToggle={() =>
              accordionStates.setInstructionExpanded(!accordionStates.instructionExpanded)
            }
            fieldGroups={instructionFieldGroups}
            onSelectAll={selectAllHandlers.handleInstructionSelectAll}
            {...getSelectAllState(instructionFieldGroups)}
          />

          <ChildAccordion
            title={translate("folder")}
            expanded={accordionStates.folderExpanded}
            onToggle={() => accordionStates.setFolderExpanded(!accordionStates.folderExpanded)}
            fieldGroups={folderFieldGroups}
            onSelectAll={selectAllHandlers.handleFolderSelectAll}
            {...getSelectAllState(folderFieldGroups)}
          />
        </ParentAccordion>
      </>
    );
  };

  return (
    <>
      <Container>{renderContent()}</Container>

      <ButtonsContainer>
        <ActionButtons {...actionButtonProps} />
      </ButtonsContainer>
    </>
  );
};
